import React, { useState } from 'react';
import { AlertCircle, FileText, ChevronRight, Send, X, Camera, Upload } from 'lucide-react';
import { User, Site, TicketType, Priority } from '../../types';
import { calculatePriority, getPriorityColor } from '../../utils/helpers';
import { Button } from '../shared';

interface TicketFormData {
    siteId: string;
    siteName: string;
    contact: string;
    type: TicketType;
    category: string;
    subcategory: string;
    impact: string;
    urgency: string;
    description: string;
    asset?: string;
    location?: string;
}

interface CreateTicketWizardProps {
    user: User;
    sites: Site[];
    onCancel: () => void;
    onSubmit: (data: TicketFormData & { priority: Priority }) => void;
}

const CATEGORIES = {
    WIFI: {
        label: 'Wi-Fi / Internet',
        subcategories: [
            'Sin conexión WiFi',
            'WiFi lento',
            'Clave WiFi olvidada',
            'Cambio de clave WiFi',
            'Agregar nuevo dispositivo',
        ],
    },
    SWITCH: {
        label: 'Switch / Rack',
        subcategories: [
            'Switch sin energía',
            'Puerto sin actividad',
            'Luces rojas en switch',
            'Switch reiniciándose',
            'Rack sin llave',
        ],
    },
    POS: {
        label: 'Cajas / Puntos de Venta',
        subcategories: [
            'Caja sin red',
            'Caja lenta',
            'Error en sistema POS',
            'Lector de código no funciona',
            'Impresora sin conexión',
        ],
    },
    BIOMETRICO: {
        label: 'Huellero / Control Asistencia',
        subcategories: [
            'No lee huellas',
            'No registra marcaje',
            'Sin conexión',
            'Pantalla negra',
            'Solicitar nuevo usuario',
        ],
    },
    ENLACE: {
        label: 'Enlace ISP (Caída Total)',
        subcategories: [
            'Sin internet completamente',
            'Internet intermitente',
            'Muy lento toda la tienda',
            'Cortado desde hace horas',
        ],
    },
    CABLEADO: {
        label: 'Cableado / Infraestructura',
        subcategories: [
            'Nuevo punto de red',
            'Cable dañado',
            'Reorganizar cableado',
            'Punto de red no funciona',
        ],
    },
    TELEFONIA: {
        label: 'Telefonía / Comunicaciones',
        subcategories: [
            'Sin tono telefónico',
            'Línea con ruido',
            'No entra/sale llamadas',
            'Agregar extensión',
        ],
    },
    OTRO: {
        label: 'Otro',
        subcategories: [
            'Consulta técnica',
            'Capacitación',
            'Otro problema',
        ],
    },
};

export const CreateTicketWizard: React.FC<CreateTicketWizardProps> = ({ user, sites, onCancel, onSubmit }) => {
    const isStore = user.role === 'STORE';
    const [step, setStep] = useState(isStore ? 2 : 1);

    const [data, setData] = useState<TicketFormData>({
        siteId: user.siteId || '',
        siteName: user.siteName || '',
        contact: user.name,
        type: 'INCIDENT',
        category: '',
        subcategory: '',
        impact: '',
        urgency: '',
        description: '',
        asset: '',
        location: '',
    });

    const currentPriority = calculatePriority(data.urgency, data.impact);
    const canAdvance = () => {
        if (step === 1) return data.siteId !== '';
        if (step === 2) return data.type && data.category;
        if (step === 3) return data.impact && data.urgency && data.description.length > 10;
        return true;
    };

    const handleNext = () => {
        if (canAdvance()) setStep(s => s + 1);
    };

    const handleBack = () => setStep(s => s - 1);

    const handleSubmit = () => {
        onSubmit({ ...data, priority: currentPriority });
    };

    const getCategorySubcategories = () => {
        if (!data.category) return [];
        return CATEGORIES[data.category as keyof typeof CATEGORIES]?.subcategories || [];
    };

    // Step 1: Location (only for non-store users)
    const renderStep1_Location = () => (
        <div className="space-y-6">
            <div>
                <h3 className="text-lg font-semibold mb-1">1. Selección de Tienda</h3>
                <p className="text-sm text-gray-500">¿Para cuál sitio es el ticket?</p>
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sitio / Tienda *</label>
                <select
                    className="w-full border rounded-lg p-3 bg-white focus:ring-2 focus:ring-black"
                    value={data.siteId}
                    onChange={e => {
                        const s = sites.find(site => site.id === e.target.value);
                        setData({ ...data, siteId: e.target.value, siteName: s?.name || '' });
                    }}
                >
                    <option value="">Seleccione sitio para el ticket...</option>
                    {sites.map(s => (
                        <option key={s.id} value={s.id}>[{s.code}] {s.name}</option>
                    ))}
                </select>
            </div>
        </div>
    );

    // Step 2: Classification
    const renderStep2_Classification = () => (
        <div className="space-y-6">
            <div>
                <h3 className="text-lg font-semibold mb-1">{isStore ? '1. ¿Qué está pasando?' : '2. Clasificación'}</h3>
                <p className="text-sm text-gray-500">Tipo de solicitud</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <button
                    onClick={() => setData({ ...data, type: 'INCIDENT' })}
                    className={`p-4 border-2 rounded-xl text-left transition ${data.type === 'INCIDENT' ? 'border-red-500 bg-red-50 ring-2 ring-red-500' : 'border-gray-200 hover:bg-gray-50'}`}
                >
                    <AlertCircle className={`mb-2 ${data.type === 'INCIDENT' ? 'text-red-500' : 'text-gray-400'}`} />
                    <div className="font-bold">Algo falló</div>
                    <div className="text-xs text-gray-500">Internet caído, equipo roto, error.</div>
                </button>
                <button
                    onClick={() => setData({ ...data, type: 'REQUEST' })}
                    className={`p-4 border-2 rounded-xl text-left transition ${data.type === 'REQUEST' ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-500' : 'border-gray-200 hover:bg-gray-50'}`}
                >
                    <FileText className={`mb-2 ${data.type === 'REQUEST' ? 'text-blue-500' : 'text-gray-400'}`} />
                    <div className="font-bold">Necesito algo</div>
                    <div className="text-xs text-gray-500">Nuevo punto de red, cables, cambios.</div>
                </button>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Categoría del problema *</label>
                <select
                    className="w-full border rounded-lg p-3 bg-white focus:ring-2 focus:ring-black"
                    value={data.category}
                    onChange={e => setData({ ...data, category: e.target.value, subcategory: '' })}
                >
                    <option value="">Seleccione categoría...</option>
                    {Object.entries(CATEGORIES).map(([key, cat]) => (
                        <option key={key} value={key}>{cat.label}</option>
                    ))}
                </select>
            </div>

            {data.category && (
                <div className="animate-in fade-in slide-in-from-bottom-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Detalle específico *</label>
                    <select
                        className="w-full border rounded-lg p-3 bg-white focus:ring-2 focus:ring-black"
                        value={data.subcategory}
                        onChange={e => setData({ ...data, subcategory: e.target.value })}
                    >
                        <option value="">Seleccione detalle...</option>
                        {getCategorySubcategories().map((sub, idx) => (
                            <option key={idx} value={sub}>{sub}</option>
                        ))}
                    </select>
                </div>
            )}
        </div>
    );

    // Step 3: Details and Priority
    const renderStep3_Details = () => (
        <div className="space-y-6">
            <div>
                <h3 className="text-lg font-semibold mb-1">{isStore ? '2. Detalle y Urgencia' : '3. Impacto y Descripción'}</h3>
                <p className="text-sm text-gray-500">Ayúdanos a priorizar</p>
            </div>

            <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-lg border border-blue-200">
                <p className="text-sm font-bold text-gray-700 mb-3">Matriz de Prioridad</p>
                <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                        <label className="block text-xs font-bold uppercase text-gray-600 mb-2">Alcance (Impacto) *</label>
                        <select
                            className="w-full text-sm border border-blue-300 p-2 rounded-lg bg-white focus:ring-2 focus:ring-blue-500"
                            value={data.impact}
                            onChange={e => setData({ ...data, impact: e.target.value })}
                        >
                            <option value="">Seleccione...</option>
                            <option value="USER">Solo a mi / Un equipo</option>
                            <option value="SECTOR">Un área (Ej: Bodega)</option>
                            <option value="SITE">Toda la tienda (Crítico)</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-bold uppercase text-gray-600 mb-2">Urgencia *</label>
                        <select
                            className="w-full text-sm border border-blue-300 p-2 rounded-lg bg-white focus:ring-2 focus:ring-blue-500"
                            value={data.urgency}
                            onChange={e => setData({ ...data, urgency: e.target.value })}
                        >
                            <option value="">Seleccione...</option>
                            <option value="LOW">Puede esperar (días)</option>
                            <option value="MEDIUM">Necesario hoy</option>
                            <option value="HIGH">Inmediata (No puedo vender)</option>
                        </select>
                    </div>
                </div>
                {data.impact && data.urgency && (
                    <div className={`p-3 rounded-lg flex justify-between items-center animate-in fade-in ${currentPriority === 'P1' ? 'bg-red-100 border-2 border-red-500' : 'bg-white border border-gray-300'}`}>
                        <span className="text-sm font-medium">Prioridad Resultante:</span>
                        <span className={`px-3 py-1 rounded-full text-sm font-bold ${getPriorityColor(currentPriority)}`}>{currentPriority}</span>
                    </div>
                )}
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">¿Qué está sucediendo? *</label>
                <textarea
                    className="w-full border rounded-lg p-3 h-32 focus:ring-2 focus:ring-black"
                    placeholder="Explique el problema con el mayor detalle posible. Ejemplo: 'La caja 3 no tiene internet desde las 10:00 AM. El switch muestra luz roja en puerto 4...'"
                    value={data.description}
                    onChange={e => setData({ ...data, description: e.target.value })}
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">{data.description.length} / mínimo 10 caracteres</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Equipo afectado (opcional)</label>
                    <input
                        type="text"
                        className="w-full border rounded-lg p-3 focus:ring-2 focus:ring-black"
                        placeholder="Ej: Caja 3, Switch principal"
                        value={data.asset}
                        onChange={e => setData({ ...data, asset: e.target.value })}
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Ubicación (opcional)</label>
                    <input
                        type="text"
                        className="w-full border rounded-lg p-3 focus:ring-2 focus:ring-black"
                        placeholder="Ej: Sala de ventas, bodega"
                        value={data.location}
                        onChange={e => setData({ ...data, location: e.target.value })}
                    />
                </div>
            </div>
        </div>
    );

    // Step 4: Evidence
    const renderStep4_Evidence = () => (
        <div className="space-y-6">
            <div>
                <h3 className="text-lg font-semibold mb-1">{isStore ? '3. Evidencias (Opcional)' : '4. Evidencias (Opcional)'}</h3>
                <p className="text-sm text-gray-500">Adjuntar fotos ayuda a resolver más rápido</p>
            </div>

            <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:bg-gray-50 transition cursor-pointer hover:border-black">
                <Camera className="mx-auto text-gray-400 mb-3" size={48} />
                <p className="text-gray-600 font-medium mb-1">Subir foto del equipo o problema</p>
                <p className="text-xs text-gray-400">Click para seleccionar archivos o arrastrar aquí</p>
                <input type="file" className="hidden" multiple accept="image/*" />
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                    <strong>Tip:</strong> Las fotos nos permiten diagnosticar más rápido. Incluye imágenes de:
                </p>
                <ul className="text-xs text-blue-700 mt-2 ml-4 list-disc">
                    <li>Luces del switch o equipo</li>
                    <li>Mensajes de error en pantalla</li>
                    <li>Vista general del problema</li>
                </ul>
            </div>
        </div>
    );

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-in fade-in">
            <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4">
                <div className="px-6 py-4 border-b flex justify-between items-center bg-gray-50">
                    <div>
                        <h2 className="text-lg font-bold">Crear Nueva Solicitud</h2>
                        <p className="text-xs text-gray-500">{isStore ? user.siteName : 'Gestión Central'}</p>
                    </div>
                    <button onClick={onCancel} className="text-gray-400 hover:text-gray-600 transition">
                        <X size={20} />
                    </button>
                </div>

                {/* Progress bar */}
                <div className="h-1 bg-gray-200 w-full">
                    <div
                        className="h-full bg-black transition-all duration-300"
                        style={{ width: `${(step / (isStore ? 3 : 4)) * 100}%` }}
                    ></div>
                </div>

                <div className="p-6 overflow-y-auto flex-1">
                    {step === 1 && !isStore && renderStep1_Location()}
                    {step === 2 && renderStep2_Classification()}
                    {step === 3 && renderStep3_Details()}
                    {step === 4 && renderStep4_Evidence()}
                </div>

                <div className="px-6 py-4 border-t bg-gray-50 flex justify-between items-center">
                    {(step > 1 && !(isStore && step === 2)) ? (
                        <Button onClick={handleBack} variant="secondary">
                            Atrás
                        </Button>
                    ) : <div></div>}

                    {step < (isStore ? 3 : 4) ? (
                        <Button onClick={handleNext} disabled={!canAdvance()}>
                            Siguiente <ChevronRight size={16} className="ml-1" />
                        </Button>
                    ) : (
                        <Button onClick={handleSubmit} variant="success" disabled={!canAdvance()}>
                            <Send size={16} className="mr-2" /> Enviar Ticket
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};
